import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { Copy, Share2 } from "lucide-react";
import { de } from "@ainkauf/core";
import { activeInvite, createInvite } from "@/db/repository";
import { config } from "@/lib/config";

/**
 * Der Einladungscode der Familie, zum Weitergeben.
 *
 * Teilen über das Systemblatt, wo es das gibt (Handy), sonst in die
 * Zwischenablage. Ohne diese Karte wüsste niemand, wie die Familie
 * dazukommt: der Code entsteht beim Anlegen der Familie in der Datenbank
 * und wäre sonst unsichtbar.
 */
export function InviteCard() {
  const invite = useLiveQuery(() => activeInvite(), [], undefined);
  const [kopiert, setKopiert] = useState(false);

  if (invite === undefined) return null;

  if (invite === null) {
    return (
      <div className="karte flex flex-col gap-3 px-5 py-5">
        <p className="text-sm text-ink-2">{de.settings.inviteNone}</p>
        <button
          type="button"
          onClick={() => void createInvite()}
          data-testid="invite-create"
          className="knopf-haupt min-h-[52px] px-4"
        >
          {de.settings.inviteCreate}
        </button>
      </div>
    );
  }

  const link = `${config().appUrl || window.location.origin}?code=${invite.code}`;
  const nachricht = de.settings.inviteMessage(invite.code, config().appUrl || window.location.origin);

  async function teilen() {
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title: de.app.name, text: nachricht, url: link });
        return;
      } catch {
        // Abgebrochen oder nicht erlaubt: dann eben die Zwischenablage.
      }
    }
    await navigator.clipboard.writeText(nachricht);
    setKopiert(true);
    window.setTimeout(() => {
      setKopiert(false);
    }, 2000);
  }

  return (
    <div className="karte flex flex-col gap-4 px-5 py-5">
      <div className="flex flex-col gap-1">
        <span className="text-sm text-ink-2">{de.settings.inviteCode}</span>
        <span className="num font-display text-[2.5rem] font-extrabold tracking-[0.15em] text-accent" data-testid="invite-code">
          {invite.code}
        </span>
        <span className="text-sm text-ink-3">
          {de.settings.inviteValidUntil(new Date(invite.expires_at).toLocaleDateString("de-DE"))}
        </span>
      </div>

      <p className="text-sm text-ink-2">{de.settings.inviteHint}</p>

      <button
        type="button"
        onClick={() => void teilen()}
        data-testid="invite-share"
        className="knopf-haupt inline-flex min-h-[52px] items-center justify-center gap-2 px-4"
      >
        {typeof navigator.share === "function" ? (
          <Share2 size={18} strokeWidth={1.5} aria-hidden />
        ) : (
          <Copy size={18} strokeWidth={1.5} aria-hidden />
        )}
        {kopiert ? de.settings.inviteCopied : typeof navigator.share === "function" ? de.settings.inviteShare : de.settings.inviteCopy}
      </button>
    </div>
  );
}
